import React, { useRef, useState } from 'react';
import { Snackbar, Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText, } from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { usersAPI } from '../services/api';

interface ExcelUploadProps {
  onUploadSuccess: () => void;
}

const ExcelUpload: React.FC<ExcelUploadProps> = ({ onUploadSuccess }) => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadResult, setUploadResult] = useState<{
    created: number;
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess('');
    setUploadResult(null);
    setLoading(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          if (jsonData.length < 2) {
            setError('فایل اکسل باید حداقل یک ردیف داده داشته باشد');
            setLoading(false);
            return;
          }

          const headers = jsonData[0].map((h: any) => String(h).trim().toLowerCase());
          
          // Find personnel code column - prioritize exact matches first
          let personnelCodeIndex = headers.findIndex((h: string) => 
            h === 'perscode' || h === 'pers_code' || h === 'personnelcode' || h === 'personnel_code'
          );
          
          if (personnelCodeIndex === -1) {
            personnelCodeIndex = headers.findIndex((h: string) => 
              h === 'prs_barcode' || h.includes('prs_barcode')
            );
          }
          
          if (personnelCodeIndex === -1) {
            personnelCodeIndex = headers.findIndex((h: string) => 
              h === 'prs_cardnum' || h.includes('prs_cardnum')
            );
          }
          
          if (personnelCodeIndex === -1) {
            personnelCodeIndex = headers.findIndex((h: string) => 
              (h.includes('کد') && h.includes('پرسنل')) || 
              h.includes('personnel') || 
              (h.includes('code') && !h.includes('mobile') && !h.includes('card')) ||
              h.includes('barcode')
            );
          }
          
          // Find first name column - prioritize exact matches first
          let firstNameIndex = headers.findIndex((h: string) => 
            h === 'fname' || h === 'f_name' || h === 'firstname' || h === 'first_name'
          );
          
          if (firstNameIndex === -1) {
            firstNameIndex = headers.findIndex((h: string) => 
              h === 'prs_firstname' || h.includes('prs_firstname')
            );
          }
          
          if (firstNameIndex === -1) {
            firstNameIndex = headers.findIndex((h: string) => 
              (h.includes('نام') && !h.includes('خانوادگی')) || 
              h === 'first name' || 
              h === 'نام' ||
              h.includes('firstname')
            );
          }
          
          // Find last name column - prioritize exact matches first
          let lastNameIndex = headers.findIndex((h: string) => 
            h === 'lname' || h === 'l_name' || h === 'lastname' || h === 'last_name'
          );
          
          if (lastNameIndex === -1) {
            lastNameIndex = headers.findIndex((h: string) => 
              h === 'prs_lastname' || h.includes('prs_lastname')
            );
          }
          
          if (lastNameIndex === -1) {
            lastNameIndex = headers.findIndex((h: string) => 
              h.includes('خانوادگی') || 
              h === 'last name' || 
              h === 'family' ||
              h.includes('lastname')
            );
          }
          
          // Find phone/mobile column - prioritize exact matches first
          let mobileIndex = headers.findIndex((h: string) => 
            h === 'phone' || h === 'mobile' || h === 'tel' || h === 'cell'
          );
          
          if (mobileIndex === -1) {
            mobileIndex = headers.findIndex((h: string) => 
              h.includes('موبایل') || 
              h.includes('mobile') || 
              h.includes('phone') || 
              h.includes('tel') ||
              h.includes('cell')
            );
          }
          
          const jobTitleIndex = headers.findIndex((h: string) => 
            h.includes('عنوان') || 
            h.includes('شغل') || 
            h.includes('job') || 
            h.includes('title') || 
            h.includes('position') ||
            h.includes('occupation')
          );
          
          // Find gender/sex column - prioritize exact matches first
          let genderIndex = headers.findIndex((h: string) => 
            h === 'sex' || h === 'gender'
          );
          
          if (genderIndex === -1) {
            genderIndex = headers.findIndex((h: string) => 
              h.includes('جنسیت') || 
              h.includes('gender') || 
              h.includes('sex')
            );
          }

          // Find company column - prioritize exact matches first
          let companyIndex = headers.findIndex((h: string) =>
            h === 'company' || h === 'comp' || h === 'org' || h === 'organization'
          );
          
          if (companyIndex === -1) {
            companyIndex = headers.findIndex((h: string) =>
              h.includes('شرکت') || h.includes('company') || h.includes('org') || h.includes('organization')
            );
          }

          // Find card number column - prioritize exact matches first
          let cardNumberIndex = headers.findIndex((h: string) =>
            h === 'card' || h === 'cardnum' || h === 'card_number' || h === 'cardnumber'
          );
          
          if (cardNumberIndex === -1) {
            cardNumberIndex = headers.findIndex((h: string) =>
              h.includes('شماره کارت') || h.includes('card') || h.includes('kart') || h.includes('cardnum')
            );
          }

          if (personnelCodeIndex === -1 || firstNameIndex === -1 || lastNameIndex === -1) {
            setError('ستون‌های مورد نیاز در فایل اکسل یافت نشد. لطفاً مطمئن شوید که فایل شامل ستون‌های: کد پرسنلی (PersCode)، نام (FName)، نام خانوادگی (LName) است.');
            setLoading(false);
            return;
          }
          
          if (mobileIndex === -1) {
            console.warn('ستون موبایل یافت نشد. از کد پرسنلی به عنوان موبایل استفاده می‌شود.');
          }

          const users = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const personnelCode = String(row[personnelCodeIndex] || '').trim();
            const firstName = String(row[firstNameIndex] || '').trim();
            const lastName = String(row[lastNameIndex] || '').trim();
            
            let mobile = '';
            if (mobileIndex !== -1) {
              mobile = String(row[mobileIndex] || '').trim();
            }
            
            if (!mobile && personnelCode) {
              const codeDigits = String(personnelCode).replace(/\D/g, '');
              
              if (codeDigits.startsWith('09') && codeDigits.length === 11) {
                mobile = codeDigits;
              } else if (codeDigits.length >= 9) {
                mobile = '09' + codeDigits.slice(-9);
              } else if (codeDigits.length > 0) {
                mobile = '09' + codeDigits.padStart(9, '0').slice(-9);
              } else {
                mobile = '0912' + String(i).padStart(7, '0');
              }
            }
            
            const jobTitle = jobTitleIndex !== -1 ? String(row[jobTitleIndex] || '').trim() : '';
            const gender = genderIndex !== -1 ? String(row[genderIndex] || '').trim() : '';
            const company = companyIndex !== -1 ? String(row[companyIndex] || '').trim() : '';
            const cardNumber = cardNumberIndex !== -1 ? String(row[cardNumberIndex] || '').trim() : '';

            if (personnelCode && firstName && lastName && mobile) {
              users.push({
                personnelCode,
                firstName,
                lastName,
                mobile,
                jobTitle,
                gender,
                company,
                cardNumber,
              });
            }
          }

          if (users.length === 0) {
            setError('هیچ داده معتبری در فایل اکسل یافت نشد');
            setLoading(false);
            return;
          }

          const result = await usersAPI.bulkUpload(users);
          
          setUploadResult({
            created: result.created,
            errors: result.errors,
          });
          
          if (result.errors && result.errors.length > 0) {
            setError(`تعداد ${result.errors.length} خطا در آپلود وجود دارد`);
          } else {
            setSuccess(`تعداد ${result.created} کاربر با موفقیت اضافه شد`);
          }

          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          if (result.created > 0) {
            setTimeout(() => {
              onUploadSuccess();
              setOpenModal(false);
            }, 2000);
          }
        } catch (err: any) {
          setError(`خطا در خواندن فایل: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      setError(`خطا در آپلود فایل: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="success"
        startIcon={<UploadFileIcon />}
        onClick={() => setOpenModal(true)}
        sx={{ fontFamily: 'Vazir, sans-serif', height: 48, borderRadius: '12px' }}
      >
        آپلود اکسل
      </Button>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          آپلود لیست پرسنل از فایل اکسل
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: 'Vazir, sans-serif', mb: 2 }}
            >
              فایل اکسل باید شامل ستون‌های زیر باشد:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="کد پرسنلی (اجباری) - Prs_Barcode یا Prs_CardNum"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="نام (اجباری) - Prs_FirstName"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="نام خانوادگی (اجباری) - Prs_LastName"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شماره موبایل (اختیاری - در صورت نبود از کد پرسنلی استفاده می‌شود)"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="عنوان شغلی (اختیاری)"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="جنسیت (اختیاری)"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شرکت (اختیاری)"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شماره کارت (اختیاری)"
                  primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.875rem' }}
                />
              </ListItem>
            </List>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'Vazir, sans-serif', fontStyle: 'italic', mt: 1, display: 'block' }}
            >
              توجه: اگر ستون موبایل وجود نداشته باشد، از کد پرسنلی به عنوان شماره موبایل استفاده می‌شود.
            </Typography>
          </Box>

          <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>

          <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

          {uploadResult && (
            <Alert severity="info" sx={{ mb: 2, fontFamily: 'Vazir, sans-serif' }}>
              <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, mb: 1 }}>
                نتیجه آپلود:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                تعداد کاربران اضافه شده: {uploadResult.created}
              </Typography>
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, mb: 0.5 }}>
                    خطاها:
                  </Typography>
                  <List dense>
                    {uploadResult.errors.slice(0, 10).map((err, idx) => (
                      <ListItem key={idx} sx={{ py: 0 }}>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                    {uploadResult.errors.length > 10 && (
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary={`و ${uploadResult.errors.length - 10} خطای دیگر...`}
                          primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={loading}
              style={{ fontFamily: 'Vazir, sans-serif' }}
            />
          </Box>

          {loading && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress />
              <Typography
                variant="body2"
                sx={{ mt: 2, fontFamily: 'Vazir, sans-serif' }}
              >
                در حال پردازش فایل...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setOpenModal(false);
              setError('');
              setSuccess('');
              setUploadResult(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            sx={{ fontFamily: 'Vazir, sans-serif' }}
          >
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExcelUpload;
